import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IDENTITY_REGEX, clampAndWrapIdentity, expandIdentityString } from "../identityFilterUtils.ts";
import {
  ChevronIcon,
  FloatingPopover,
  GridIcon,
  OPERATOR_DEFAULT_LABEL,
  SearchIcon,
  SuggestRow,
  getInitialIdText,
  getNoun,
  groupValuesSorted,
  sharedStyles as s,
  useGroupData,
  useIdentityEmit,
  type VariantProps,
} from "./shared.tsx";

/**
 * Variation A — "Inline fields".
 * Connected ID + Search input group (visually joined); "Bulk select" behind
 * a popover button. Closest to today's look, lowest migration risk.
 *
 * ## Embedded mode (opt-in)
 *
 * When the variant is hosted inside an editor that already renders its own
 * item list (see `pages/ingress/ingressGroupPage.tsx`), the suggestion
 * popover and the Apply/Cancel bulk dance get in the way. Pass
 * `embedded` + `searchText` + `onSearchTextChange` to:
 *
 *   1. Suppress the suggestion popover; the search text is forwarded to
 *      the host (which prefilters its list externally).
 *   2. Make the Bulk popover apply immediately on toggle — no Add/Cancel.
 *   3. The host renders its own "+ Add N matched" / "- Remove N matched"
 *      buttons next to its segmented filters — same placement convention
 *      used for non-identity panes — so those affordances stay where the
 *      user expects.
 *   4. The ID range and Search inputs are *transient prefilter* — they
 *      always start empty and never mirror the host's selection (would
 *      otherwise dump every selected id into the visible field on
 *      mount). Bulk popover toggles still mutate the selection via
 *      `onChange` so multi-select shortcuts work.
 *
 * All `embedded*` props are optional; default behaviour for the grid filter
 * bar is unchanged.
 */
interface InlineVariantExtraProps {
  embedded?: boolean;
  /** Controlled search text. Embedded host owns it (free-text prefilter). */
  searchText?: string;
  onSearchTextChange?: (text: string) => void;
  /** Forward typed ID range to host so it can apply it as a prefilter. */
  onIdTextChange?: (text: string) => void;
  /** Host's current selection — used by the Bulk popover to compute
   *  per-group checkbox state in embedded mode. */
  selectedIds?: number[];
  /** Bulk popover toggle in embedded mode — host applies to selection.
   *  Omit it (and `selectedIds`) when the host's "selection" IS the ID-range
   *  prefilter (e.g. the location map): toggles then add/remove the group's
   *  ids in the ID field and reach the host through `onIdTextChange`. */
  onSelectionToggle?: (ids: number[], on: boolean) => void;
}

export function InlineVariant<T>({
  config,
  columnName,
  isParameter,
  value,
  onChange,
  onSearch,
  embedded = false,
  searchText,
  onSearchTextChange,
  onIdTextChange,
  selectedIds,
  onSelectionToggle,
}: VariantProps<T> & InlineVariantExtraProps) {
  /* In embedded mode, idText is purely transient (host owns the selection
     elsewhere). Don't seed from the value prop and don't sync back when
     value changes — that would dump every selected id into the field. */
  const [idText, setIdText] = useState<string>(() => (embedded ? "" : getInitialIdText(value)));
  const [internalSearch, setInternalSearch] = useState("");
  const search = embedded ? (searchText ?? "") : internalSearch;
  const setSearch = embedded ? (onSearchTextChange ?? (() => {})) : setInternalSearch;
  const [showSuggest, setShowSuggest] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>(config.groupFields?.[0]?.field ?? "");
  /* Non-embedded mode only: selections keep one checked-value set per group
     field (not just the active tab) so choosing e.g. Retail Host AND Type
     narrows the result to kiosks matching both (AND across fields, OR within
     a field's values) instead of unioning every field's matches together. */
  const [groupSelections, setGroupSelections] = useState<Map<string, Set<string>>>(new Map());
  const [bulkSearch, setBulkSearch] = useState("");

  const suggestAnchorRef = useRef<HTMLDivElement>(null);
  const bulkAnchorRef = useRef<HTMLDivElement>(null);

  const emit = useIdentityEmit(columnName, isParameter, onChange);
  const groupData = useGroupData(config);
  const noun = getNoun(config);

  useEffect(() => {
    /* Embedded mode: do NOT mirror value into idText. Selection lives in
       the host; the inline ID input is a transient filter slot. */
    if (embedded) return;
    if (value.length === 0) {
      setIdText("");
    } else if (value[0]?.oValue != null) {
      setIdText(String(value[0].oValue));
    }
  }, [value, embedded]);

  const update = useCallback(
    (text: string) => {
      setIdText(text);
      if (embedded) {
        /* Forward to host so it can use the typed range as a prefilter
           (intersected with the search text). Selection is untouched. */
        onIdTextChange?.(text);
        return;
      }
      if (IDENTITY_REGEX.test(text) || !text) emit(text);
    },
    [emit, embedded, onIdTextChange],
  );

  const ids = useMemo(() => expandIdentityString(idText), [idText]);

  const addId = useCallback(
    (id: number) => {
      const current = expandIdentityString(idText);
      if (current.includes(id)) return;
      update(clampAndWrapIdentity([...current, id]));
    },
    [idText, update],
  );

  const addIds = useCallback(
    (toAdd: number[]) => {
      if (!toAdd.length) return;
      const current = expandIdentityString(idText);
      const set = new Set(current);
      toAdd.forEach((id) => set.add(id));
      update(clampAndWrapIdentity(Array.from(set)));
    },
    [idText, update],
  );

  const removeIds = useCallback(
    (toRemove: number[]) => {
      if (!toRemove.length) return;
      const current = expandIdentityString(idText);
      const removeSet = new Set(toRemove);
      const next = current.filter((id) => !removeSet.has(id));
      update(clampAndWrapIdentity(next));
    },
    [idText, update],
  );

  const suggestions = useMemo(() => {
    if (!search.trim()) return [];
    const lc = search.toLowerCase();
    const out: T[] = [];
    for (const it of config.items) {
      if (config.matchFn(it, lc)) {
        out.push(it);
        if (out.length >= 30) break;
      }
    }
    return out;
  }, [search, config]);

  const groupValues = useMemo(
    () => groupValuesSorted(groupData, selectedGroup),
    [groupData, selectedGroup],
  );

  const filteredGroupValues = useMemo(() => {
    if (!bulkSearch.trim()) return groupValues;
    const lc = bulkSearch.toLowerCase();
    return groupValues.filter((g) => g.value.toLowerCase().includes(lc));
  }, [groupValues, bulkSearch]);

  const toggleGroupValue = useCallback((field: string, val: string) => {
    setGroupSelections((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(field));
      if (set.has(val)) set.delete(val); else set.add(val);
      if (set.size) next.set(field, set); else next.delete(field);
      return next;
    });
  }, []);

  /* AND across fields, OR within a field: union the ids matching any checked
     value of a given field, then intersect those per-field sets together. */
  const bulkMatchIds = useMemo(() => {
    if (!groupData || groupSelections.size === 0) return [] as number[];
    let result: Set<number> | null = null;
    for (const [field, vals] of groupSelections) {
      const m = groupData.get(field);
      if (!m) return [];
      const fieldIds = new Set<number>();
      for (const v of vals) {
        const list = m.get(v);
        if (list) for (const id of list) fieldIds.add(id);
      }
      if (result === null) result = fieldIds;
      else {
        const next = new Set<number>();
        for (const id of result) if (fieldIds.has(id)) next.add(id);
        result = next;
      }
    }
    return result ? [...result] : [];
  }, [groupData, groupSelections]);

  const applyBulk = useCallback(() => {
    if (!bulkMatchIds.length) return;
    update(clampAndWrapIdentity([...expandIdentityString(idText), ...bulkMatchIds]));
    setBulkOpen(false);
    setGroupSelections(new Map());
  }, [bulkMatchIds, idText, update]);

  /* In embedded mode, toggling a group value applies immediately:
     check → add all of that group's ids; uncheck → remove them. The popover
     stays open so the user can flip several values rapidly. */
  const toggleBulkGroupValue = useCallback(
    (val: string, on: boolean) => {
      const m = groupData?.get(selectedGroup);
      const list = m?.get(val);
      if (!list || !list.length) return;
      /* Embedded: route to host's selection mutator. */
      if (onSelectionToggle) {
        onSelectionToggle(list, on);
        return;
      }
      /* Fallback (no host wire-up): mutate the inline filter value. */
      if (on) addIds(list);
      else removeIds(list);
    },
    [groupData, selectedGroup, addIds, removeIds, onSelectionToggle],
  );

  /* In embedded mode, "selected" for a group value means every id in that
     group is in the host's selection — partial groups still render as
     unchecked (toggling on adds the missing ones). */
  const groupValueSelected = useCallback(
    (val: string): boolean => {
      const list = groupData?.get(selectedGroup)?.get(val);
      if (!list || !list.length) return false;
      const source = selectedIds ?? expandIdentityString(idText);
      const current = new Set(source);
      return list.every((id) => current.has(id));
    },
    [groupData, selectedGroup, idText, selectedIds],
  );

  /* outside-click handling is wired inside each FloatingPopover */

  const isValid = !idText || IDENTITY_REGEX.test(idText);

  return (
    <div style={{
      display: "inline-flex",
      alignItems: "stretch",
      border: `1px solid ${isValid ? "var(--color-border-default)" : "var(--color-error)"}`,
      borderRadius: "var(--radius-1)",
      background: "var(--color-bg)",
      width: "fit-content",
      // Never wider than the container (e.g. a narrow Filters modal) — the search
      // field below flexes down so the whole control shrinks to fit instead of
      // overflowing the panel edge.
      maxWidth: "100%",
      position: "relative",
    }}>
      {/* ID label */}
      <div style={{
        display: "flex", alignItems: "center",
        padding: "0 var(--space-1) 0 var(--space-2)",
        color: "var(--color-text-muted)",
        borderRight: "1px solid var(--color-border-default)",
        fontSize: "var(--text-xs)", fontWeight: 600, letterSpacing: "0.04em",
      }}>
        ID
      </div>

      {/* ID input */}
      <input
        value={idText}
        onChange={(e) => update(e.target.value.replace(/[^0-9,\-]/g, ""))}
        onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
        placeholder="1,3,5-10"
        aria-label="ID range"
        style={{
          width: 110, minWidth: 0, border: "none", padding: "0 var(--space-2)", height: 28,
          font: "inherit", fontSize: "var(--text-sm)", outline: "none",
          fontVariantNumeric: "tabular-nums", background: "transparent",
          color: "var(--color-text)",
        }}
      />

      {/* Search input */}
      <div ref={suggestAnchorRef} style={{
        position: "relative",
        borderLeft: "1px solid var(--color-border-default)",
        display: "flex", alignItems: "center",
        flex: "1 1 200px", minWidth: 0,
      }}>
        <span style={{ padding: "0 var(--space-0-5) 0 var(--space-2)", color: "var(--color-text-muted)", display: "inline-flex", alignItems: "center" }}>
          <SearchIcon size={13} />
        </span>
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); if (!embedded) setShowSuggest(true); }}
          onFocus={() => !embedded && search.trim() && setShowSuggest(true)}
          onKeyDown={(e) => {
            if (embedded) return;
            if (e.key === "Enter" && suggestions.length) {
              const newIds = suggestions.map(config.getIdFromItem);
              update(clampAndWrapIdentity([...ids, ...newIds]));
              setSearch(""); setShowSuggest(false);
              // Defer onSearch past this render: update() queues an onChange
              // up to AdvancedFilterBar → useGridData's filtersRef only sees
              // the new value after React commits + the parent useEffect
              // re-emits. Calling onSearch synchronously here makes the
              // first Enter run with stale filters (the second press worked
              // because state had committed by then). rAF mirrors the
              // pattern dataGrid.tsx uses for the same problem.
              requestAnimationFrame(() => onSearch());
            } else if (e.key === "Escape") setShowSuggest(false);
          }}
          placeholder={`Search ${noun}s…`}
          style={{
            flex: 1, minWidth: 0, border: "none", padding: "0 var(--space-2) 0 var(--space-1)", height: 28,
            font: "inherit", fontSize: "var(--text-sm)", outline: "none",
            background: "transparent", color: "var(--color-text)",
          }}
        />
        <FloatingPopover
          open={!embedded && showSuggest && suggestions.length > 0}
          anchorRef={suggestAnchorRef}
          onClose={() => setShowSuggest(false)}
          width={360}
          className={s.popover}
        >
          <div style={{
            padding: "var(--space-0-5) var(--space-2)", fontSize: "var(--text-xs)",
            color: "var(--color-text-muted)",
            borderBottom: "1px solid var(--color-bg-muted)",
            display: "flex", justifyContent: "space-between",
          }}>
            <span>{suggestions.length} match{suggestions.length === 1 ? "" : "es"}</span>
            <span>Press <span className={s.kbd}>↵</span> to add all</span>
          </div>
          <div style={{ maxHeight: 260, overflow: "auto" }}>
            {suggestions.map((it, i) => (
              <SuggestRow
                key={i}
                item={it}
                config={config}
                onClick={() => { addId(config.getIdFromItem(it)); setSearch(""); setShowSuggest(false); }}
              />
            ))}
          </div>
        </FloatingPopover>
      </div>

      {/* Bulk-select (only when groupFields exist) */}
      {config.groupFields && (
        <>
          <div style={{ width: 1, background: "var(--color-border-default)" }} />
          <div ref={bulkAnchorRef} style={{ position: "relative" }}>
            <button
              type="button"
              className={s.btnGhost}
              style={{ height: 28, borderRadius: 0, padding: "0 var(--space-2)" }}
              onClick={() => setBulkOpen((o) => !o)}
            >
              <GridIcon /> Bulk <ChevronIcon size={9} />
            </button>
            <FloatingPopover
              open={bulkOpen}
              anchorRef={bulkAnchorRef}
              onClose={() => setBulkOpen(false)}
              align="end"
              width={320}
              className={s.popover}
            >
              <div style={{ padding: "var(--space-2)" }}>
                <div style={{
                  fontSize: "var(--text-xs)", color: "var(--color-text-muted)",
                  fontWeight: 600, letterSpacing: "0.04em",
                  textTransform: "uppercase", marginBottom: "var(--space-1)",
                }}>
                  Bulk add by
                </div>
                <div className={s.seg} style={{ flexWrap: "wrap", marginBottom: "var(--space-2)" }}>
                  {config.groupFields.map((gf) => (
                    <button
                      type="button"
                      key={gf.field}
                      className={selectedGroup === gf.field ? s.segBtnActive : s.segBtn}
                      onClick={() => { setSelectedGroup(gf.field); setBulkSearch(""); }}
                    >
                      {gf.label}
                      {!embedded && groupSelections.get(gf.field)?.size ? (
                        <span className={s.countBadge}>{groupSelections.get(gf.field)!.size}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
                {!embedded && groupSelections.size > 1 && (
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: "var(--space-1)" }}>
                    Matching all selected fields (AND).
                  </div>
                )}
                <input
                  className={s.input}
                  style={{ width: "100%", height: 26, marginBottom: "var(--space-1)" }}
                  value={bulkSearch}
                  onChange={(e) => setBulkSearch(e.target.value)}
                  placeholder={`Filter ${groupValues.length} value${groupValues.length === 1 ? "" : "s"}…`}
                />
                <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)" }}>
                  {filteredGroupValues.map((g) => {
                    const checked = embedded
                      ? groupValueSelected(g.value)
                      : (groupSelections.get(selectedGroup)?.has(g.value) ?? false);
                    return (
                      <label key={g.value} className={s.check}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (embedded) {
                              toggleBulkGroupValue(g.value, !checked);
                            } else {
                              toggleGroupValue(selectedGroup, g.value);
                            }
                          }}
                        />
                        <span style={g.value === OPERATOR_DEFAULT_LABEL ? { fontStyle: "italic" } : undefined}>{g.value}</span>
                        <span className={s.countBadge}>{g.count}</span>
                      </label>
                    );
                  })}
                  {!filteredGroupValues.length && (
                    <div style={{ padding: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                      {groupValues.length ? "No matches." : "No values."}
                    </div>
                  )}
                </div>
                {!embedded && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--space-2)", gap: "var(--space-1)" }}>
                    <button type="button" className={s.btnGhost} onClick={() => { setBulkOpen(false); setGroupSelections(new Map()); }}>
                      Cancel
                    </button>
                    <button type="button" className={s.btnPrimary} onClick={applyBulk} disabled={!bulkMatchIds.length}>
                      Add {bulkMatchIds.length || ""} {noun}s
                    </button>
                  </div>
                )}
              </div>
            </FloatingPopover>
          </div>
        </>
      )}
    </div>
  );
}
